import { Badge, Button, Group, Select, Stack, Switch, Text, Textarea, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  JOB_INSIGHT_SOURCE_LABELS,
  JOB_INSIGHT_TYPE_LABELS,
  JOB_INSIGHT_TYPE_OPTIONS,
  REJECTION_INSIGHT_MIN_FEEDBACKS,
} from "../../constants/recruiterAi";
import { adminApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import type { JobInsight, JobInsightType } from "../../types/api";
import { JobInsightAlerts } from "../../components/jobs/JobInsightsBanner";
import { LoadingOverlay } from "../../components/ui";
import { axioTokens } from "../../theme";

const GENERATE_LABEL = "Generate AI insight";
const PUBLISH_LABEL = "Published";
const DELETE_LABEL = "Delete";
const SAVE_LABEL = "Save insight";
const CONTENT_LABEL = "Recruiter-facing content";
const NOTE_LABEL = "Internal note";
const TYPE_LABEL = "Type";
const EMPTY_COPY = "No insights yet. Add a note or generate a rejection-pattern draft.";
const NEW_INSIGHT_TITLE = "New insight";
const PUBLISHED_TITLE = "Published on the recruiter job page";
const PUBLISHED_EMPTY = "Nothing published. Toggle Published on a draft to show it to recruiters.";
const BANNER_PREVIEW_LABEL = "Recruiter banner";
const GENERATE_HINT = `AI generation needs at least ${REJECTION_INSIGHT_MIN_FEEDBACKS} rejected submissions with feedback. Drafts stay unpublished until you toggle them.`;

interface Props {
  jobId: string;
}

export function JobInsightsPanel({ jobId }: Props) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<JobInsightType>("hiring_criteria");
  const [content, setContent] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [published, setPublished] = useState(false);

  const insightsQuery = useQuery({
    queryKey: ["admin-job-insights", jobId],
    queryFn: () => adminApi.listJobInsights(jobId),
    enabled: !!jobId,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createJobInsight(jobId, {
        insight_type: type,
        content,
        internal_note: internalNote || undefined,
        published,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Insight saved" });
      setContent("");
      setInternalNote("");
      setPublished(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-job-insights", jobId] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not save insight",
      });
    },
  });

  const generateMutation = useMutation({
    mutationFn: () => adminApi.generateJobInsight(jobId),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Draft insight generated" });
      void queryClient.invalidateQueries({ queryKey: ["admin-job-insights", jobId] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not generate insight",
      });
    },
  });

  const publishMutation = useMutation({
    mutationFn: ({ insight, next }: { insight: JobInsight; next: boolean }) =>
      adminApi.updateJobInsight(jobId, insight.id, { published: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-job-insights", jobId] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not update insight",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (insightId: string) => adminApi.deleteJobInsight(jobId, insightId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-job-insights", jobId] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not delete insight",
      });
    },
  });

  const insights = insightsQuery.data ?? [];
  const publishedInsights = insights.filter((insight) => insight.published);

  return (
    <Stack gap={axioTokens.unit[32]}>
      <Stack gap="md">
        <Title order={5}>{PUBLISHED_TITLE}</Title>
        {publishedInsights.length > 0 ? (
          <JobInsightAlerts insights={publishedInsights} />
        ) : (
          <Text size="sm" c="dimmed">
            {PUBLISHED_EMPTY}
          </Text>
        )}
      </Stack>

      <Stack gap="md">
        <Title order={5}>{NEW_INSIGHT_TITLE}</Title>
        <Select
          label={TYPE_LABEL}
          data={JOB_INSIGHT_TYPE_OPTIONS}
          value={type}
          onChange={(value) => value && setType(value as JobInsightType)}
        />
        <Textarea label={CONTENT_LABEL} minRows={5} value={content} onChange={(e) => setContent(e.currentTarget.value)} />
        <Textarea
          label={NOTE_LABEL}
          minRows={2}
          value={internalNote}
          onChange={(e) => setInternalNote(e.currentTarget.value)}
        />
        <Switch label={PUBLISH_LABEL} checked={published} onChange={(e) => setPublished(e.currentTarget.checked)} />
        <Group>
          <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending} disabled={!content.trim()}>
            {SAVE_LABEL}
          </Button>
          <Button variant="default" onClick={() => generateMutation.mutate()} loading={generateMutation.isPending}>
            {GENERATE_LABEL}
          </Button>
        </Group>
        <Text size="sm" c="dimmed">
          {GENERATE_HINT}
        </Text>
      </Stack>

      {insightsQuery.isLoading ? (
        <LoadingOverlay height="var(--axio-unit-160)" />
      ) : insights.length === 0 ? (
        <Text c="dimmed" size="sm">
          {EMPTY_COPY}
        </Text>
      ) : (
        <Stack gap={axioTokens.unit[32]}>
          {insights.map((insight) => (
            <InsightRow
              key={insight.id}
              jobId={jobId}
              insight={insight}
              onPublish={(next) => publishMutation.mutate({ insight, next })}
              onDelete={() => deleteMutation.mutate(insight.id)}
              deleting={deleteMutation.isPending && deleteMutation.variables === insight.id}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function InsightRow({
  jobId,
  insight,
  onPublish,
  onDelete,
  deleting,
}: {
  jobId: string;
  insight: JobInsight;
  onPublish: (next: boolean) => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState(insight.content);
  const [internalNote, setInternalNote] = useState(insight.internal_note ?? "");

  useEffect(() => {
    setContent(insight.content);
    setInternalNote(insight.internal_note ?? "");
  }, [insight.content, insight.internal_note]);

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.updateJobInsight(jobId, insight.id, {
        content,
        internal_note: internalNote || null,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Insight updated" });
      void queryClient.invalidateQueries({ queryKey: ["admin-job-insights", jobId] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not update insight",
      });
    },
  });

  const dirty = content !== insight.content || internalNote !== (insight.internal_note ?? "");

  return (
    <Stack gap="md" p="lg" className="jobInsightItem">
      <Group justify="space-between" wrap="wrap" gap="md">
        <Group gap="xs">
          <Badge variant="outline">{JOB_INSIGHT_TYPE_LABELS[insight.insight_type] ?? insight.insight_type}</Badge>
          <Badge variant="light" color="gray">
            {JOB_INSIGHT_SOURCE_LABELS[insight.source] ?? insight.source}
          </Badge>
        </Group>
        <Group>
          <Switch label={PUBLISH_LABEL} checked={insight.published} onChange={(e) => onPublish(e.currentTarget.checked)} />
          <Button variant="subtle" color="red" onClick={onDelete} loading={deleting}>
            {DELETE_LABEL}
          </Button>
        </Group>
      </Group>
      <Textarea label={CONTENT_LABEL} minRows={4} value={content} onChange={(e) => setContent(e.currentTarget.value)} />
      <Textarea label={NOTE_LABEL} minRows={2} value={internalNote} onChange={(e) => setInternalNote(e.currentTarget.value)} />
      <Stack gap="md">
        <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
          {BANNER_PREVIEW_LABEL}
        </Text>
        <JobInsightAlerts
          insights={[
            {
              id: insight.id,
              insight_type: insight.insight_type,
              content,
              based_on_count: insight.based_on_count,
            },
          ]}
        />
      </Stack>
      <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!content.trim() || !dirty}>
        {SAVE_LABEL}
      </Button>
    </Stack>
  );
}
